import type { DashboardSnapshot } from "./dashboard";
import type { ReadinessTarget } from "./readiness-types";

export function isPropertyReadinessTarget(target: ReadinessTarget | null | undefined): boolean {
  return target?.kind === "property" || target?.property_id != null;
}

const SYSTEM_TARGET_LABELS = new Set(["onboarding"]);

export function isSystemReadinessTargetLabel(
  label: string | null | undefined,
) {
  return SYSTEM_TARGET_LABELS.has((label ?? "").trim().toLowerCase());
}

export function normalizeReadinessTargetLabel(
  label: string | null | undefined,
) {
  const normalized = (label ?? "").trim();
  return isSystemReadinessTargetLabel(normalized) ? "" : normalized;
}

export function displayReadinessTargetArea(
  target: ReadinessTarget | null | undefined,
  fallbackArea: string,
) {
  const fallback = fallbackArea.trim();
  if (!target) {
    return fallback;
  }

  const locationCode = target.deal.property.location_code?.trim() ?? "";
  if (isSystemReadinessTargetLabel(target.label) && fallback) {
    return fallback;
  }

  return normalizeReadinessTargetLabel(target.label) || locationCode || fallback;
}

export function summaryLocationForReadinessTarget(
  target: ReadinessTarget,
  fallbackArea: string,
) {
  const fallback = fallbackArea.trim();
  const locationCode = target.deal.property.location_code?.trim() ?? "";

  if (isSystemReadinessTargetLabel(target.label) && fallback) {
    return fallback;
  }

  if (locationCode && locationCode.length !== 2) {
    return locationCode;
  }

  return normalizeReadinessTargetLabel(target.label) || fallback || locationCode;
}

export function snapshotWithActiveTargetDisplay(
  snapshot: DashboardSnapshot,
  target: ReadinessTarget | null | undefined,
): DashboardSnapshot {
  if (!target) {
    return snapshot;
  }

  const trackedProperty =
    target.kind === "property" && target.property_id
      ? snapshot.trackedProperties?.find(
          (property) => property.id === target.property_id,
        )
      : null;
  const rawTargetPrice = target.deal.property.target_price;
  const targetPrice =
    trackedProperty?.listPrice ??
    (typeof rawTargetPrice === "number" && rawTargetPrice > 0
      ? Math.round(rawTargetPrice / 100)
      : snapshot.targetPrice);
  const targetArea =
    trackedProperty?.address ||
    displayReadinessTargetArea(target, snapshot.targetArea);

  if (
    targetPrice === snapshot.targetPrice &&
    targetArea === snapshot.targetArea
  ) {
    return snapshot;
  }

  return {
    ...snapshot,
    targetArea,
    targetPrice,
  };
}
